import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, TrendingUp, TrendingDown, BookText } from "lucide-react";

import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";

/**
 * Iter 66b — Cash Register report.
 *
 * Traditional cash-book: chronological money in / money out for a single
 * cash-family account with running balance. Powered by
 * `/api/reports/cash-register?account_code=...&start=...&end=...`.
 */
export default function CashRegister() {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [accounts, setAccounts] = useState([]);
  const [accountCode, setAccountCode] = useState("");
  const [start, setStart] = useState(firstOfMonth.toISOString().slice(0, 10));
  const [end, setEnd] = useState(today.toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await api.get("/payments/deposit-accounts");
      setAccounts(data);
      if (data.length && !accountCode) {
        // Default: the first Asset code (e.g. 1000 Cash & Bank)
        const asset = data.find((a) => a.type === "Asset");
        setAccountCode(asset?.code || data[0].code);
      }
    } catch {
      toast.error("Failed to load accounts");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const load = useCallback(async () => {
    if (!accountCode || !start || !end) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/reports/cash-register?account_code=${accountCode}&start=${start}&end=${end}`
      );
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load cash register");
    } finally {
      setLoading(false);
    }
  }, [accountCode, start, end]);
  useEffect(() => { load(); }, [load]);

  const currency = data?.currency || "USD";
  const netChange = data ? (data.total_in - data.total_out) : 0;

  const exportCSV = () => {
    if (!data) return;
    const header = ["Date", "Reference", "Memo", "Source", "Money In", "Money Out", "Balance"];
    const rows = data.rows.map((r) => [
      r.date, r.reference, r.memo?.replace(/,/g, ";"), r.source,
      r.money_in, r.money_out, r.balance,
    ]);
    const csv = [header, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cash-register-${data.account_code}-${start}-${end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const summary = useMemo(() => {
    if (!data) return null;
    return [
      { label: "Opening", value: data.opening_balance, tone: "muted" },
      { label: "Money in", value: data.total_in, tone: "up" },
      { label: "Money out", value: data.total_out, tone: "down" },
      { label: "Net change", value: netChange, tone: netChange >= 0 ? "up" : "down" },
      { label: "Closing", value: data.closing_balance, tone: "brand" },
    ];
  }, [data, netChange]);

  return (
    <AppShell>
      <div className="space-y-6" data-testid="cash-register-page">
        <header className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Reports</div>
            <h1 className="font-display text-3xl">Cash register</h1>
            <p className="text-sm text-[var(--muted-text)] mt-1">
              Chronological money in / money out for any cash-family account, with running balance.
            </p>
          </div>
          <div className="w-11 h-11 rounded-lg bg-[#E8F1EE] text-[#2F4F4F] flex items-center justify-center">
            <BookText className="w-5 h-5" />
          </div>
        </header>

        <div className="card-flat p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Account</Label>
              <Select value={accountCode} onValueChange={setAccountCode}>
                <SelectTrigger data-testid="cr-account" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Start date</Label>
              <Input data-testid="cr-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>End date</Label>
              <Input data-testid="cr-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1" />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline" onClick={exportCSV} disabled={!data} data-testid="cr-export"
                className="gap-1.5"
              ><Download className="w-3.5 h-3.5" /> Export CSV</Button>
            </div>
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {summary.map((s) => (
              <div key={s.label} className="card-flat p-4">
                <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">{s.label}</div>
                <div className={`text-xl font-mono-data mt-1 ${
                  s.tone === "up" ? "text-[#4C956C]" :
                  s.tone === "down" ? "text-[#B4283A]" :
                  s.tone === "brand" ? "text-[#2F4F4F]" : ""
                }`}>{formatCurrency(s.value, currency)}</div>
              </div>
            ))}
          </div>
        )}

        <div className="card-flat overflow-hidden">
          <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider bg-[var(--hover)] text-[var(--muted-text)] border-b border-[var(--divider)]">
            <div className="col-span-2">Date</div>
            <div className="col-span-2">Reference</div>
            <div className="col-span-3">Memo</div>
            <div className="col-span-1">Source</div>
            <div className="col-span-1 text-right">In</div>
            <div className="col-span-1 text-right">Out</div>
            <div className="col-span-2 text-right">Balance</div>
          </div>
          {loading && (
            <div className="px-4 py-8 text-center text-sm text-[var(--muted-text)]">Loading…</div>
          )}
          {!loading && data && data.rows.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-[var(--muted-text)]">
              No cash movement in this account during the selected period.
              Opening balance: <span className="font-mono-data">{formatCurrency(data.opening_balance, currency)}</span>
            </div>
          )}
          {!loading && data && data.rows.map((r, i) => (
            <div key={r.reference || `${r.date}-${r.memo}-${i}`} data-testid={`cr-row-${i}`} className="grid grid-cols-12 px-4 py-2 text-sm border-b border-[var(--divider)] last:border-b-0 hover:bg-[var(--row-hover)]">
              <div className="col-span-2 font-mono-data">{r.date}</div>
              <div className="col-span-2 text-xs text-[var(--muted-text)]">{r.reference}</div>
              <div className="col-span-3 text-xs">{r.memo}</div>
              <div className="col-span-1">
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F5F1E9] text-[#7A6C4E]">{r.source}</span>
              </div>
              <div className="col-span-1 text-right font-mono-data text-[#4C956C]">
                {r.money_in > 0 ? formatCurrency(r.money_in, currency) : ""}
              </div>
              <div className="col-span-1 text-right font-mono-data text-[#B4283A]">
                {r.money_out > 0 ? formatCurrency(r.money_out, currency) : ""}
              </div>
              <div className="col-span-2 text-right font-mono-data font-medium">{formatCurrency(r.balance, currency)}</div>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
